import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { problemCode } from '@up2d8/api-client';
import type { AccountDeletionOverview, AccountDeletionScope } from '@up2d8/contracts';
import { AppSubShell, Button, Callout, Card, CardTitle, Input, LoadingState } from '@up2d8/ui';
import { formatDate } from '../../shared/format';
import { useSession } from '../../shared/session';
import { PIN_LENGTH, PinPad } from './pin-pad';

/**
 * Account verwijderen, in de app (DEL-1, ADR-0098).
 *
 * De app- en de Play-winkel eisen dat dit in de app zelf kan. Eerst staat er wat er gebeurt, dan
 * de keuze (alleen dit bedrijf of alles, als er meer zijn), dan het wachtwoord of de pincode als
 * bevestiging. Een vinkje "ik begrijp het" is er bewust niet: het wachtwoord intypen is al de
 * bewuste handeling, en een vinkje dat iedereen aanklikt zegt niets.
 *
 * Na het verzoek is de sessie op de server al ingetrokken; dit scherm toont nog de bevestiging en
 * meldt dan lokaal af.
 */
export function AppAccountDeletionPage() {
  const { t } = useTranslation();
  const { api, logout } = useSession();
  const navigate = useNavigate();

  const overview = useQuery({
    queryKey: ['me', 'account-deletion'],
    queryFn: async (): Promise<AccountDeletionOverview> => {
      const { data, error } = await api.GET('/api/v1/me/account-deletion');
      if (error) throw error;
      return data as AccountDeletionOverview;
    },
  });

  const [scope, setScope] = useState<AccountDeletionScope>('tenant');
  const [secret, setSecret] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ eraseAfter: string | null; accounts: number } | null>(null);

  const back = () => void navigate({ to: '/app/settings' });

  const submit = async () => {
    setError(null);
    setBusy(true);
    try {
      const { data, error: failure } = await api.POST('/api/v1/me/account-deletion', {
        body: { scope, secret },
      });
      if (failure) {
        setSecret('');
        const code = problemCode(failure);
        const detail = (failure as { detail?: string }).detail;
        if (code === 'VALIDATION_FAILED') {
          setError(
            overview.data?.verification === 'pin'
              ? t('accountDeletion.app.wrongPin')
              : t('accountDeletion.app.wrongPassword'),
          );
        } else if (code === 'LAST_ADMIN' || code === 'FORBIDDEN') {
          setError(detail ?? t('accountDeletion.app.refused'));
        } else if (code === 'RATE_LIMITED') {
          setError(t('appLogin.locked'));
        } else {
          setError(t('accountDeletion.app.failed'));
        }
        return;
      }
      setDone({ eraseAfter: data?.eraseAfter ?? null, accounts: data?.accounts ?? 0 });
    } finally {
      setBusy(false);
    }
  };

  // De sessie is op de server al weg; lokaal opruimen en naar het aanmeldscherm.
  const leave = () => void logout().finally(() => void navigate({ to: '/app/login' }));

  if (done) {
    return (
      <AppSubShell title={t('accountDeletion.title')} onBack={leave}>
        <Card>
          <CardTitle>{t('accountDeletion.app.doneTitle')}</CardTitle>
          <p className="mt-element text-content text-text-secondary" role="status">
            {t('accountDeletion.app.doneText', {
              date: done.eraseAfter ? formatDate(done.eraseAfter) : '',
            })}
          </p>
          {overview.data?.hasEmail ? (
            <p className="mt-element text-content text-text-secondary">
              {t('accountDeletion.app.doneMail')}
            </p>
          ) : null}
          <Button className="mt-section w-full" onClick={leave}>
            {t('accountDeletion.app.close')}
          </Button>
        </Card>
      </AppSubShell>
    );
  }

  const data = overview.data;
  const pin = data?.verification === 'pin';
  const several = (data?.organisations.length ?? 0) > 1;
  const current = data?.organisations.find((organisation) => organisation.current);
  const ready = pin ? secret.length === PIN_LENGTH : secret.length > 0;

  return (
    <AppSubShell title={t('accountDeletion.title')} onBack={back}>
      {overview.isPending ? (
        <LoadingState label={t('common.loading')} />
      ) : !data ? (
        <Callout tone="danger">{t('accountDeletion.app.failed')}</Callout>
      ) : (
        <>
          <Card>
            <CardTitle>{t('accountDeletion.whatHappens')}</CardTitle>
            <ul className="mt-element list-disc space-y-1 pl-5 text-content text-text-secondary">
              <li>{t('accountDeletion.steps.immediately')}</li>
              <li>{t('accountDeletion.steps.erased', { days: data.graceDays })}</li>
              <li>{t('accountDeletion.steps.kept')}</li>
              <li>
                {data.hasEmail
                  ? t('accountDeletion.steps.cancelByMail')
                  : t('accountDeletion.steps.cancelNoMail')}
              </li>
            </ul>
          </Card>

          <Card>
            {several ? (
              <fieldset className="flex flex-col gap-inline">
                <legend className="mb-element font-semibold text-text-primary">
                  {t('accountDeletion.app.scopeLegend')}
                </legend>
                <label className="flex items-start gap-inline text-content">
                  <input
                    type="radio"
                    name="scope"
                    checked={scope === 'tenant'}
                    onChange={() => setScope('tenant')}
                    className="mt-1"
                  />
                  <span>{t('accountDeletion.app.scopeTenant', { name: current?.name ?? '' })}</span>
                </label>
                <label className="flex items-start gap-inline text-content">
                  <input
                    type="radio"
                    name="scope"
                    checked={scope === 'all'}
                    onChange={() => setScope('all')}
                    className="mt-1"
                  />
                  <span>
                    {t('accountDeletion.app.scopeAll', {
                      names: data.organisations.map((organisation) => organisation.name).join(', '),
                    })}
                  </span>
                </label>
              </fieldset>
            ) : null}

            <div className={several ? 'mt-section' : undefined}>
              {pin ? (
                <PinPad value={secret} onChange={setSecret} label={t('accountDeletion.app.pin')} />
              ) : (
                <Input
                  label={t('accountDeletion.app.password')}
                  type="password"
                  autoComplete="current-password"
                  value={secret}
                  onChange={(event) => setSecret(event.target.value)}
                />
              )}
            </div>

            {error ? (
              <p
                role="alert"
                className="mt-element rounded-md bg-danger-tint px-3 py-2 text-content text-danger"
              >
                {error}
              </p>
            ) : null}

            <Button
              variant="danger"
              className="mt-section w-full"
              loading={busy}
              disabled={!ready}
              onClick={() => void submit()}
            >
              {t('accountDeletion.app.submit')}
            </Button>
            <Link to="/app/settings" className="mt-element block text-center text-content">
              {t('common.cancel')}
            </Link>
          </Card>
        </>
      )}
    </AppSubShell>
  );
}
